import { SignatoryEntry } from '@/modules/templates/lib/signatory-config';
import { ensureAdditionalSignatoryArray } from '@/modules/templates/lib/signatory-fields';
import { LegalDocument } from '@/schemas/schema';
import { FileText, Heading, List, Type } from 'lucide-react';

type Translate = (key: string, values?: Record<string, string | number | Date>) => string;

/** Get a friendly label for the block type */
export function getBlockLabel(
  t: Translate,
  blockType?: string,
  isTitle?: boolean
): { label: string; Icon: typeof Type } {
  if (isTitle) {
    return { label: t('GENERATEREVIEW_BLOCKLABELS_HEADING'), Icon: Heading };
  }

  switch (blockType) {
    case 'article':
      return {
        label: t('GENERATEREVIEW_BLOCKLABELS_ARTICLETITLE'),
        Icon: Heading,
      };
    case 'section':
      return {
        label: t('GENERATEREVIEW_BLOCKLABELS_SECTIONTITLE'),
        Icon: Heading,
      };
    case 'paragraph':
      return {
        label: t('GENERATEREVIEW_BLOCKLABELS_PARAGRAPH'),
        Icon: FileText,
      };
    case 'list_item':
      return { label: t('GENERATEREVIEW_BLOCKLABELS_LISTITEM'), Icon: List };
    case 'definition_item':
      return { label: t('GENERATEREVIEW_BLOCKLABELS_DEFINITION'), Icon: Type };
    case 'table_cell':
      return { label: t('GENERATEREVIEW_BLOCKLABELS_TABLECELL'), Icon: Type };
    case 'effectiveDate':
      return {
        label: t('GENERATEREVIEW_BLOCKLABELS_EFFECTIVEDATE'),
        Icon: Type,
      };
    default:
      return { label: t('GENERATEREVIEW_BLOCKLABELS_TEXT'), Icon: Type };
  }
}

/** A signatory as surfaced in the review UI (extracted from doc/form data). */
export type Signatory = { name: string; email: string; role: string; color: string };

/** Latest signing event for a signatory, keyed by email in the review UI. */
export type SignatoryEvent = { eventType: string; occurredAt: string; signedTime?: string };

// Color palette for signatories (aligned with brand tokens)
export const SIGNATORY_COLORS = [
  'hsl(var(--selise-blue))',
  'hsl(var(--poly-green))',
  'hsl(var(--mauveine))',
  'hsl(var(--destructive))',
];

// Helper to extract signatories from document or form data
export function extractSignatories(
  document: LegalDocument,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>,
  defaults: {
    defaultRole: string;
    companyName: string;
    authorizedRepresentativeRole: string;
    employeeRole: string;
  }
): Array<{ name: string; email: string; role: string; color: string }> {
  const signatories: Array<{
    name: string;
    email: string;
    role: string;
    color: string;
  }> = [];

  // 1. First priority: document.signatories (should be set correctly from form data by generate API)
  if (document.signatories && document.signatories.length > 0) {
    document.signatories.forEach((sig, index) => {
      signatories.push({
        name: sig.name,
        email: sig.email || '',
        role: sig.title || sig.party,
        color: SIGNATORY_COLORS[index % SIGNATORY_COLORS.length],
      });
    });
    return signatories;
  }

  // If document doesn't have signatories, fall back to form data

  const addSignatory = ({ name, email, role }: { name: string; email: string; role?: string }) => {
    if (!name || !email) return;
    signatories.push({
      name,
      email,
      role: role || defaults.defaultRole,
      color: SIGNATORY_COLORS[signatories.length % SIGNATORY_COLORS.length],
    });
  };

  // 2. NEW Signatory Screen format (formData.signatories array from SignatoryScreenRenderer)
  if (Array.isArray(formData.signatories) && formData.signatories.length > 0) {
    (formData.signatories as SignatoryEntry[]).forEach((entry) => {
      addSignatory({
        name: entry.name,
        email: entry.email,
        role: entry.title || entry.partyType,
      });
    });
    return signatories;
  }

  // 3. Form builder signatory screen fields (party, name, email, title, phone)
  if (formData.name && formData.email) {
    addSignatory({
      name: formData.name as string,
      email: formData.email as string,
      role: (formData.title as string) || (formData.party as string),
    });
  }

  const additionalEntries = ensureAdditionalSignatoryArray(formData.additionalSignatories);
  additionalEntries.forEach((entry) => {
    addSignatory({
      name: entry.name,
      email: entry.email,
      role: entry.title || entry.party,
    });
  });

  if (signatories.length > 0) {
    return signatories;
  }

  // 3. Check for numbered signatory pattern (signatory_1_name, signatory_2_name, etc.)
  let signatoryIndex = 1;
  while (formData[`signatory_${signatoryIndex}_name`]) {
    addSignatory({
      name: formData[`signatory_${signatoryIndex}_name`] as string,
      email: (formData[`signatory_${signatoryIndex}_email`] as string) || '',
      role:
        (formData[`signatory_${signatoryIndex}_title`] as string) ||
        (formData[`signatory_${signatoryIndex}_party`] as string),
    });
    signatoryIndex++;
  }
  if (signatories.length > 0) {
    return signatories;
  }

  // 4. Legacy hardcoded field names (for backward compatibility)
  // Company/Employer signatory
  if (formData.companyRepName || formData.companyName) {
    signatories.push({
      name:
        (formData.companyRepName as string) ||
        (formData.companyName as string) ||
        defaults.companyName,
      email: (formData.companyRepEmail as string) || (formData.companyEmail as string) || '',
      role: (formData.companyRepTitle as string) || defaults.authorizedRepresentativeRole,
      color: SIGNATORY_COLORS[0],
    });
  }

  // Employee signatory
  if (formData.employeeName) {
    signatories.push({
      name: formData.employeeName as string,
      email: (formData.employeeEmail as string) || '',
      role: (formData.jobTitle as string) || defaults.employeeRole,
      color: SIGNATORY_COLORS[1],
    });
  }

  return signatories;
}

// Helper function to check if effectiveDate is a valid date
export function isValidEffectiveDate(effectiveDate: string | undefined): boolean {
  if (!effectiveDate) return false;

  // Check if it's a text value like "Upon Signature"
  const textValues = [
    'upon signature',
    'upon signing',
    'upon execution',
    'date of signature',
    'date of signing',
  ];
  if (textValues.includes(effectiveDate.toLowerCase().trim())) {
    return false;
  }

  // Check if it's a valid date
  const date = new Date(effectiveDate);
  return !isNaN(date.getTime()) && date.toString() !== 'Invalid Date';
}
